import { get, set } from 'idb-keyval'
import { apiClient } from '@/api/client'

/**
 * Pre-reserved order numbers, so an offline order prints its FINAL number on
 * the kitchen slip and nothing has to be matched up by hand afterwards.
 *
 * Blocks are keyed to a business date. A block for a past date is dead — those
 * numbers belong to a day that has closed — so it is released rather than used.
 * Reserving for tomorrow as well is deliberately NOT done: the business date
 * boundary is per-tenant (app_settings.day_boundary_hour), so the client cannot
 * compute tomorrow's date reliably. Instead the pool refills on every online
 * poll, which covers the "outage started before opening" case for any terminal
 * that was online at some point during the previous service.
 */

const POOL_KEY = 'bhookly-offline-order-numbers-v1'

/** Enough for a multi-hour outage at the busiest observed tenant. */
export const DEFAULT_BLOCK_SIZE = 20

export interface OrderNumberPool {
  /** ISO business date the block belongs to, e.g. "2026-08-05". */
  businessDate: string
  numbers: string[]
}

export async function readPool(): Promise<OrderNumberPool | null> {
  return (await get<OrderNumberPool>(POOL_KEY)) ?? null
}

/** How many usable numbers this terminal holds for `todayISO`. */
export async function poolSize(todayISO: string): Promise<number> {
  const pool = await readPool()
  if (!pool || pool.businessDate !== todayISO) return 0
  return pool.numbers.length
}

/**
 * Pop one number. Returns null when the pool is empty or belongs to another
 * business date — a stale number would collide with a closed day's sequence.
 */
export async function takeReservedNumber(todayISO: string): Promise<string | null> {
  const pool = await readPool()
  if (!pool || pool.businessDate !== todayISO || pool.numbers.length === 0) return null
  const [next, ...rest] = pool.numbers
  await set(POOL_KEY, { businessDate: pool.businessDate, numbers: rest })
  return next
}

/**
 * Top the pool back up while online.
 *
 * A block from a previous day is handed back first: keeping it would leave
 * permanent gaps in that day's invoice sequence, which invoice-sequence-check
 * reports as missing filings. Release is best-effort — a failed release only
 * leaves a gap the server can still reclaim, whereas throwing here would block
 * the refill and leave the terminal unable to sell offline at all.
 */
export async function refillPool(
  todayISO: string,
  target = DEFAULT_BLOCK_SIZE,
): Promise<void> {
  const pool = await readPool()

  if (pool && pool.businessDate !== todayISO) {
    if (pool.numbers.length > 0) {
      try {
        await apiClient.releaseOrderNumbers(pool.numbers)
      } catch {
        /* best-effort — the server can still reclaim the gap */
      }
    }
    await set(POOL_KEY, { businessDate: todayISO, numbers: [] })
  }

  const current = (await readPool())?.numbers ?? []
  const need = target - current.length
  if (need <= 0) return

  const res = await apiClient.reserveOrderNumbers(need)
  const fresh = res.data?.numbers ?? []
  if (fresh.length === 0) return
  await set(POOL_KEY, { businessDate: todayISO, numbers: [...current, ...fresh] })
}

/**
 * Hand the whole block back, e.g. at day close. Clears locally regardless of
 * whether the server call succeeded — holding numbers the terminal will never
 * use is worse than a reclaimable gap.
 */
export async function releaseEntirePool(): Promise<void> {
  const pool = await readPool()
  if (pool && pool.numbers.length > 0) {
    try {
      await apiClient.releaseOrderNumbers(pool.numbers)
    } catch {
      /* best-effort */
    }
  }
  if (pool) await set(POOL_KEY, { businessDate: pool.businessDate, numbers: [] })
}
